import {
  Background,
  BackgroundVariant,
  Edge,
  Node,
  NodeTypes,
  ReactFlow,
  ReactFlowProvider,
  useEdgesState,
  useNodesState,
  useReactFlow,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import Dagre from '@dagrejs/dagre';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  createEntityNode,
  createEventEdge,
  createSourceEntityEdge,
  createDelegationEdge,
  NodeData,
} from './nodes/nodesUtils';
import { EntityType } from '@/components/entities/entityType';
import PreviewNode from './nodes/previewNode';
import { PipelineInstance } from '@/interfaces/pipelineInstance.interface';

const NODE_W  = 120;
const NODE_H  = 30;
const RANK_SEP = 50;
const PREVIEW_HEIGHT = 90;

const previewNodeTypes: NodeTypes = {
  [EntityType.dataSourceInstance]: PreviewNode,
  [EntityType.agentInstance]:      PreviewNode,
  [EntityType.userAppInstance]:    PreviewNode,
};

/** Layout with fixed dimensions — no DOM measurement required. */
const layoutNodes = (nodes: Node<NodeData>[], edges: Edge[], direction: 'LR' | 'TB' = 'LR') => {
  if (nodes.length === 0) return { nodes, edges, spanW: 0, spanH: 0 };

  const g = new Dagre.graphlib.Graph().setDefaultEdgeLabel(() => ({}));
  g.setGraph({
    rankdir: direction,
    ranksep: direction === 'TB' ? 45 : RANK_SEP,
    nodesep: direction === 'TB' ? 35 : 20,
  });

  nodes.forEach((n) => g.setNode(n.id, { width: NODE_W, height: NODE_H }));
  edges.forEach((e) =>
    g.setEdge(e.source, e.target, { label: '' }),
  );

  Dagre.layout(g);

  let maxX = 0;
  let maxY = 0;

  const laidOutNodes = nodes.map((n) => {
    const pos = g.node(n.id);
    const x = pos.x - NODE_W / 2;
    const y = pos.y - NODE_H / 2;
    maxX = Math.max(maxX, x + NODE_W);
    maxY = Math.max(maxY, y + NODE_H);
    return { ...n, position: { x, y } };
  });

  return { nodes: laidOutNodes, edges, spanW: maxX, spanH: maxY };
};

interface PipelinePreviewFlowProps {
  pipeline: PipelineInstance;
  height?: number | string;
  direction?: 'LR' | 'TB';
}

const PreviewFlowInner = ({ pipeline, height, direction = 'LR' }: PipelinePreviewFlowProps) => {
  const theme = useTheme();
  const containerHeight = height || PREVIEW_HEIGHT;
  const { fitView } = useReactFlow<Node<NodeData>, Edge>();
  const [nodes, setNodes] = useNodesState<Node<NodeData>>([]);
  const [edges, setEdges] = useEdgesState<Edge>([]);
  const [loading, setLoading] = useState(true);
  const fitPending = useRef(false);

  const deriveEdges = useCallback(
    (allNodes: Node<NodeData>[]) => {
      const result: Edge[] = [];
      allNodes.forEach((n) => {
        (n.data.entity?.eventSubscriptions || []).forEach((es: any) => {
          const edge = createEventEdge(es, theme.palette.primary.main);
          if (direction === 'TB') {
            edge.sourceHandle = 'bottom';
            edge.targetHandle = 'top';
          }
          result.push(edge);
        });
        (n.data.entity?.sourceEntities || []).forEach((se: any) => {
          const edge = createSourceEntityEdge(se, theme.palette.primary.main);
          if (direction === 'TB') {
            edge.sourceHandle = 'bottom';
            edge.targetHandle = 'top';
          }
          result.push(edge);
        });
        // Dynamic Subagent Delegation Edges (Dashed Purple)
        const supervisorEntity = n.data.entity;
        const delegatedAgentIds =
          supervisorEntity?.delegatedAgentIds ||
          supervisorEntity?.settings?.delegatedAgentIds ||
          supervisorEntity?.subagentIds ||
          [];
        if (Array.isArray(delegatedAgentIds)) {
          delegatedAgentIds.forEach((targetAgentId: string) => {
            const targetNode = allNodes.find(
              (other) =>
                other.id === targetAgentId ||
                other.data.entity?.id === targetAgentId ||
                other.data.entity?.agentDefId === targetAgentId,
            );
            if (targetNode) {
              const edge = createDelegationEdge(n.id, targetNode.id, 'Delegates', '#9333ea');
              if (direction === 'TB') {
                edge.sourceHandle = 'bottom';
                edge.targetHandle = 'top';
              }
              result.push(edge);
            }
          });
        }
      });
      return result;
    },
    [theme.palette.primary.main, direction],
  );

  useEffect(() => {
    const rawItems = pipeline?.items || (pipeline as any)?.entities || [];
    if (!rawItems || rawItems.length === 0) {
      setNodes([]);
      setEdges([]);
      setLoading(false);
      return;
    }

    const build = async () => {
      setLoading(true);
      try {
        const items: any[] = Array.isArray(rawItems) ? rawItems : [];
        const rawNodes: Node<NodeData>[] = [];

        for (const item of items) {
          const node = await createEntityNode([], item, typeof containerHeight === 'number' ? containerHeight : PREVIEW_HEIGHT, true);
          node.data.viewOnly = true;
          node.draggable = false;
          node.type = item.entityType; // Force to base entity type for preview rendering
          rawNodes.push(node);
        }

        const rawEdges = deriveEdges(rawNodes).map((e) => ({ ...e, label: undefined }));
        const { nodes: laid, edges: laidEdges } = layoutNodes(rawNodes, rawEdges, direction);

        setNodes(laid);
        setEdges(laidEdges);
        fitPending.current = true;
      } catch (err) {
        console.error('Failed to build preview nodes', err);
      } finally {
        setLoading(false);
      }
    };

    build();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pipeline?.id, pipeline?.items, (pipeline as any)?.entities, direction]);

  useEffect(() => {
    if (loading || !fitPending.current) return;
    fitPending.current = false;
    const t = setTimeout(() => fitView({ duration: 300, padding: 0.06 }), 60);
    return () => clearTimeout(t);
  }, [loading, nodes, fitView]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: containerHeight }}>
        <CircularProgress size={20} color="secondary" />
      </Box>
    );
  }

  if (nodes.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: containerHeight,
          bgcolor: 'rgba(0,0,0,0.02)',
          borderRadius: '10px',
          border: '1px dashed',
          borderColor: 'divider',
        }}
      >
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
          No nodes in this pipeline.
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: '100%',
        height: containerHeight,
        borderRadius: '10px',
        overflow: 'hidden',
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: '#f8f9fb',
        '& .react-flow__node':   { cursor: 'pointer !important' },
        '& .react-flow__handle': { pointerEvents: 'none !important' },
        '& .react-flow__edge':   { pointerEvents: 'none !important' },
      }}
    >
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={previewNodeTypes}
        nodesDraggable={false}
        nodesConnectable={false}
        elementsSelectable={true}
        panOnDrag={false}
        panOnScroll={false}
        zoomOnScroll={false}
        zoomOnDoubleClick={false}
        preventScrolling={false}
        proOptions={{ hideAttribution: true }}
        fitView
        fitViewOptions={{ padding: 0.06 }}
        minZoom={0.2}
        maxZoom={1.0}
        style={{ width: '100%', height: '100%' }}
      >
        <Background variant={BackgroundVariant.Dots} gap={14} size={1} color={theme.palette.divider} />
      </ReactFlow>
    </Box>
  );
};

const PipelinePreviewFlow = (props: PipelinePreviewFlowProps) => (
  <ReactFlowProvider>
    <PreviewFlowInner {...props} />
  </ReactFlowProvider>
);

export default PipelinePreviewFlow;
